// RefundModal.js
import React, { useState, useEffect } from 'react';
import './RefundModal.css';

const RefundModal = ({
  isOpen,
  onClose,
  onSubmit,
  orderID,
  paymentMethod,
  transactionStatus,
  amount,
  currency,
  cardBrand,
  stripePaymentIntent,
}) => {
  const [refundType, setRefundType] = useState('full');
  const [partialAmount, setPartialAmount] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setRefundType('full');
      setPartialAmount('');
      setError('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const numericAmount = parseFloat(amount) || 0;

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (refundType === 'partial') {
      const val = parseFloat(partialAmount);
      if (isNaN(val) || val <= 0) {
        setError('Please enter a valid amount greater than 0.');
        return;
      }
      if (val >= numericAmount) {
        setError(`Partial amount must be less than the full amount (${numericAmount} ${currency}).`);
        return;
      }
      onSubmit({ refundType: 'partial', amount: val });
    } else {
      onSubmit({ refundType: 'full', amount: null });
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="header-content">
            <h3>Issue Refund</h3>
          </div>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        {/* Transaction Info Card */}
        <div className="user-info-card">
          <div className="info-row">
            <div className="info-item">
              <span className="info-label">Order ID</span>
              <span className="info-value">{orderID || '—'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Status</span>
              <span className="info-value">
                <span className="badge">{transactionStatus || '—'}</span>
              </span>
            </div>
          </div>
          <div className="info-row">
            <div className="info-item">
              <span className="info-label">Amount</span>
              <span className="info-value">
                {numericAmount.toFixed(2)} {currency}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">Payment</span>
              <span className="info-value">
                {cardBrand ? `${cardBrand} · ${paymentMethod}` : paymentMethod || '—'}
              </span>
            </div>
          </div>
          {stripePaymentIntent && (
            <div className="info-row">
              <div className="info-item">
                <span className="info-label">Stripe PI</span>
                <span className="info-value stripe-pi">{stripePaymentIntent}</span>
              </div>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-body">
            {/* Refund type toggle */}
            <div className="form-group">
              <label>Refund type <span className="required">*</span></label>
              <div className="refund-type-row">
                <button
                  type="button"
                  className={`refund-type-btn${refundType === 'full' ? ' active' : ''}`}
                  onClick={() => { setRefundType('full'); setError(''); }}
                >
                  Full refund
                  <span className="refund-type-amount">
                    {numericAmount.toFixed(2)} {currency}
                  </span>
                </button>
                <button
                  type="button"
                  className={`refund-type-btn${refundType === 'partial' ? ' active' : ''}`}
                  onClick={() => { setRefundType('partial'); setError(''); }}
                >
                  Partial refund
                </button>
              </div>
            </div>

            {/* Partial amount input */}
            {refundType === 'partial' && (
              <div className="form-group">
                <label htmlFor="partialAmount">
                  Amount to refund <span className="required">*</span>
                  <span className="optional">(max {numericAmount.toFixed(2)} {currency})</span>
                </label>
                <div className="partial-input-wrap">
                  <span className="partial-currency">{currency}</span>
                  <input
                    type="number"
                    id="partialAmount"
                    min="0.01"
                    max={numericAmount - 0.01}
                    step="0.01"
                    value={partialAmount}
                    onChange={(e) => { setPartialAmount(e.target.value); setError(''); }}
                    placeholder={`0.00`}
                    autoFocus
                  />
                </div>
              </div>
            )}

            {error && <div className="refund-error">{error}</div>}

            {/* Warning */}
            <div className="refund-warning">
              ⚠️ This action will issue a{refundType === 'full' ? ' full' : ' partial'} refund
              via Stripe and cannot be undone.
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-submit btn-refund">
              Confirm refund
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RefundModal;